import { onLCP, onCLS, onFCP, onTTFB, onINP, type Metric } from 'web-vitals';
import { appInsights } from 'src/Services/Insights';
import { GetUserAppInsightProperties } from './AppInsightProperties';

/**
 * Tracks the current route path for SPA route-level context.
 * Updated by `startRouteTracking` on each navigation.
 */
const hasWindowLocation =
  typeof window !== 'undefined' && typeof window.location !== 'undefined';
let currentRoute: string = hasWindowLocation ? window.location.pathname : '';
let fullRoutePath: string = hasWindowLocation ? window.location.href : '';
let domainName: string = hasWindowLocation ? window.location.hostname : '';
let queryString: string = hasWindowLocation ? window.location.search : '';

interface WebVitalContext {
  route: string;
  fullRoutePath: string;
  domainName: string;
  queryString: string;
  userAgent: string;
  connectionType: string;
  deviceMemory: string;
  hardwareConcurrency: number;
}

/**
 * Gathers useful environmental context for each Web Vital metric.
 */
function getWebVitalContext(): WebVitalContext {
  const nav = navigator as any;
  const connection = nav.connection || nav.mozConnection || nav.webkitConnection;

  return {
    route: currentRoute,
    fullRoutePath: fullRoutePath,
    domainName: domainName,
    queryString: queryString,
    userAgent: navigator.userAgent,
    connectionType: connection?.effectiveType || 'unknown',
    deviceMemory: nav.deviceMemory ? `${nav.deviceMemory}GB` : 'unknown',
    hardwareConcurrency: navigator.hardwareConcurrency || 0,
  };
}

/**
 * Sends a Web Vital metric to Application Insights with full context.
 */
function sendMetricToAppInsights(metric: Metric): void {
  const context = getWebVitalContext();
  const userProps = GetUserAppInsightProperties();

  // Track as a custom event with detailed properties
  const details = {
    name: `WebVital_${metric.name}`,
    properties: {
      ...userProps,
      metricName: metric.name,
      metricValue: String(metric.value),
      metricRating: metric.rating,
      metricDelta: String(metric.delta),
      metricId: metric.id,
      navigationType: metric.navigationType || 'unknown',
      route: context.route,
      fullRoutePath: context.fullRoutePath.split('?')[0].split('#')[0],
      domainName: context.domainName,
      hasQueryString: context.queryString.length > 0,
      queryStringKeyCount: String(new URLSearchParams(context.queryString).size),
      userAgent: context.userAgent,
      connectionType: context.connectionType,
      deviceMemory: context.deviceMemory,
      hardwareConcurrency: String(context.hardwareConcurrency),
      timestamp: new Date().toISOString(),
    },
  };
  appInsights.trackEvent(details);
}

/**
 * Initializes Web Vitals tracking and sends metrics to Application Insights.
 * Call this once during app initialization.
 *
 * - LCP uses `reportAllChanges: true` to capture intermediate updates
 *   (e.g., when a larger element renders after the initial paint).
 * - Other metrics report only the final value.
 */
export function initWebVitalsTracking(): void {
  // LCP with reportAllChanges to capture progressive rendering updates
  onLCP(sendMetricToAppInsights, { reportAllChanges: true });

  // Core Web Vitals
  onCLS(sendMetricToAppInsights);
  onINP(sendMetricToAppInsights);

  // Supplementary metrics
  onFCP(sendMetricToAppInsights);
  onTTFB(sendMetricToAppInsights);
}

/**
 * Updates the current route for route-level Web Vitals tracking in a React SPA.
 * Call this inside a `useEffect` that listens to location changes (e.g., via react-router).
 *
 * @param pathname - The new route pathname (e.g., from `useLocation().pathname`)
 */
export function updateRouteForTracking(pathname: string): void {
  currentRoute = pathname;
  fullRoutePath = window.location.href;
  domainName = window.location.hostname;
  queryString = window.location.search;
}

/**
 * React hook that integrates Web Vitals route tracking with react-router.
 * Place this in a component that wraps your routes (e.g., App or Layout).
 *
 * Usage:
 * ```tsx
 * import { useWebVitalsRouteTracking } from 'src/utils/webVitalsTracker';
 * import { useLocation } from 'react-router-dom';
 *
 * function App() {
 *   const location = useLocation();
 *   useWebVitalsRouteTracking(location.pathname);
 *   // ...
 * }
 * ```
 */
export function useWebVitalsRouteTracking(pathname: string): void {
  // Note: This is intentionally not using React hooks (useEffect) directly
  // to avoid requiring React as a dependency in this utility module.
  // The caller should invoke this from within a useEffect.
  if (pathname !== currentRoute) {
    updateRouteForTracking(pathname);
  }
}
// Generated by GitHub Copilot | Updated on: 2026-05-19 | 120 lines modified | Created Web Vitals tracker with LCP reporting, App Insights integration, context enrichment, and SPA route tracking
